/* ============================================================================
   TENDER ROOM — access-code sessions.
   One code logs each party in: the manager code, a client passcode per tender,
   or a bidder access code. Session persists in localStorage.
   ========================================================================== */
import { useEffect, useState } from "react";
import { getTenders, STUDIO, actions } from "./store";
import { getProjects, pmActions } from "./pmStore";
import { getStudioTeam, studioTeamActions } from "./studioTeam";
import { getSettings, passphraseActive, sha256Hex } from "./settingsStore";
import { serverManagerLogin, serverCodeLogin, setManagerToken } from "./managerAuth";
import { kickSync } from "./sync";
import { isStudioAdmin, isCommsManager } from "./studioRoles";
import { fullAccess } from "./permissions";
import type { BidTeamRole, ClientRole, StudioRole, TeamRole } from "./types";

/* `studioRole` rides alongside the primary role rather than replacing it: a
   Studio Admin needs a bid-shaped session to work a tender AND project access,
   and the union below is discriminated by module. Carrying the studio grade as
   a second field is what lets one person hold both. */
export type Session =
  | { role: "manager"; name: string }
  | { role: "client"; tenderId: string; name: string; company: string; seatId?: string; clientRole?: ClientRole }
  | { role: "bidder"; tenderId: string; bidderId: string; name: string; company: string }
  | { role: "bidteam"; tenderId: string; memberId: string; name: string; teamRole: BidTeamRole; studioRole?: StudioRole }  // studio-side tender team
  | { role: "contractor"; projectId: string; name: string; company: string; teamRole?: TeamRole | (string & {}); studioRole?: StudioRole }
  | { role: "pclient"; projectId: string; name: string; company: string; clientRole?: ClientRole }   // project-side client
  | { role: "sub"; projectId: string; packageId: string; inviteId: string; name: string; company: string };  // subcontractor — one package's room only

const KEY = "bidroom.session.v1";

function load(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

let session: Session | null = load();
const subs = new Set<() => void>();

function commit(next: Session | null) {
  session = next;
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch { /* ignore */ }
  subs.forEach((f) => f());
}

export function useSession(): Session | null {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return session;
}

export function getSession() { return session; }
export function logout() { setManagerToken(null); commit(null); }

/** Try a credential against the manager passphrase / manager code / client
    passcodes / bidder & team access codes. Returns the session or null. */
export async function loginWithCode(raw: string): Promise<Session | null> {
  const exact = raw.trim();            // passphrases are case-sensitive
  const code = exact.toUpperCase();
  if (!code) return null;

  // The studio account. The passphrase is checked SERVER-side (the hash never
  // syncs down, and a fresh device can't even know one is set) — so ALWAYS try
  // the server first; it also honours the default studio credentials while no
  // passphrase exists. Plain local dev falls back to the local settings.
  const settings = getSettings();
  const r = await serverManagerLogin(exact);
  if (r === "ok") {
    const s: Session = { role: "manager", name: STUDIO.manager };
    commit(s);
    kickSync();
    return s;
  }
  if (r === "offline") {
    const localOk = passphraseActive(settings)
      ? !!settings.passphraseHash && (await sha256Hex(exact)) === settings.passphraseHash
      : code === STUDIO.managerCode || code === STUDIO.managerEmail.toUpperCase();
    if (localOk) {
      const s: Session = { role: "manager", name: STUDIO.manager };
      commit(s);
      kickSync();
      return s;
    }
  }
  // "bad" / "unset" / no local match — the input may still be an access code

  // Access codes are verified by the SERVER (the workspace no longer syncs to
  // signed-out clients, so there's nothing local to scan on a fresh device).
  // The local scan below stays as the offline / plain-dev fallback.
  const srv = await serverCodeLogin(exact);
  if (srv && srv !== "offline") {
    const s = srv.session as Session;
    commit(s);
    kickSync();
    return s;
  }
  if (srv === null) return null;   // the server is authoritative: no such code
  // Studio team — full-access Site Admins invited from the Projects list.
  // No home project of their own, hence the empty projectId; the admin
  // guards let them into every room.
  const st = getStudioTeam().find((m) => m.accessCode.toUpperCase() === code);
  if (st) {
    studioTeamActions.markActive(st.id);
    const role = st.role ?? "site-admin";
    const s: Session = role === "tender-admin" || role === "tender-coordinator"
      ? { role: "bidteam", tenderId: "", memberId: st.id, name: st.name, teamRole: role === "tender-admin" ? "admin" : "coordinator" }
      : role === "comms-manager"
        // every project, but as a coordinator of its conversations — not an admin of its works
        ? { role: "contractor", projectId: "", name: st.name, company: `${STUDIO.name} ${STUDIO.unit}`, teamRole: "consultant", studioRole: "comms-manager" }
        : { role: "contractor", projectId: "", name: st.name, company: `${STUDIO.name} ${STUDIO.unit}`, teamRole: "admin" };
    commit(s);
    return s;
  }
  for (const p of getProjects()) {
    if (p.contractor.accessCode.toUpperCase() === code) {
      const s: Session = { role: "contractor", projectId: p.id, name: p.contractor.name, company: p.contractor.company, teamRole: "contractor" };
      commit(s);
      return s;
    }
    // subcontractors invited to price (or run) one trade package
    for (const pkg of p.packages ?? []) {
      const inv = (pkg.invites ?? []).find((i) => i.accessCode.toUpperCase() === code);
      if (inv) {
        pmActions.markSubActive(p.id, pkg.id, inv.id);
        const s: Session = { role: "sub", projectId: p.id, packageId: pkg.id, inviteId: inv.id, name: inv.contact, company: inv.company };
        commit(s);
        return s;
      }
    }
    const member = (p.team ?? []).find((m) => m.accessCode.toUpperCase() === code);
    if (member) {
      pmActions.markMemberActive(p.id, member.id);
      const s: Session = member.role === "client"
        ? { role: "pclient", projectId: p.id, name: member.name, company: member.company, clientRole: member.clientRole ?? "principal" }
        : { role: "contractor", projectId: p.id, name: member.name, company: member.company, teamRole: member.role };
      commit(s);
      return s;
    }
  }
  for (const t of getTenders()) {
    if (t.client.passcode.toUpperCase() === code) {
      // legacy shared passcode — the client rep, full portal
      const s: Session = { role: "client", tenderId: t.id, name: t.client.name, company: t.client.company, clientRole: "principal" };
      commit(s);
      return s;
    }
    const seat = (t.clientTeam ?? []).find((x) => x.accessCode.toUpperCase() === code);
    if (seat) {
      actions.markClientSeatActive(t.id, seat.id);
      const s: Session = { role: "client", tenderId: t.id, name: seat.name, company: t.client.company, seatId: seat.id, clientRole: seat.role };
      commit(s);
      return s;
    }
    const tm = (t.team ?? []).find((m) => m.accessCode.toUpperCase() === code);
    if (tm) {
      actions.markBidTeamActive(t.id, tm.id);
      const s: Session = { role: "bidteam", tenderId: t.id, memberId: tm.id, name: tm.name, teamRole: tm.role };
      commit(s);
      return s;
    }
    const b = t.bidders.find((x) => x.accessCode.toUpperCase() === code);
    if (b) {
      if (b.status === "invited") actions.registerBidder(t.id, b.id);
      else actions.touchBidder(t.id, b.id);
      const s: Session = { role: "bidder", tenderId: t.id, bidderId: b.id, name: b.contact, company: b.company };
      commit(s);
      return s;
    }
  }
  return null;
}

export function homeFor(s: Session): string {
  if (s.role === "manager") return "/";
  // a Studio Admin owns both modules, so no single home is right — the landing
  // page keeps its module cards and asks which one they want
  if (isStudioAdmin(s)) return "/";
  if (isCommsManager(s)) return "/pm/comms";
  if (s.role === "bidteam") return s.tenderId ? `/manage/${s.tenderId}/dashboard` : "/manage";
  if (s.role === "client") return `/client/${s.tenderId}`;
  // A contractor lands on their project picker (one login, choose the job); the
  // picker forwards straight in when they're only on one. Studio admins get the list.
  if (s.role === "contractor") return s.teamRole === "admin" ? "/pm" : "/pm/select";
  if (s.role === "pclient") return `/site/${s.projectId}`;
  if (s.role === "sub") return `/sub/${s.projectId}/${s.packageId}`;
  return `/bid/${s.tenderId}`;
}

/* ------------------------------------------------------- module access -- */
/* Which of the two cockpits a session may open. Guards, the landing cards and
   the module shells all read these, so there is one answer per question. */

/** The Bid Manager — every tender room. */
export function canOpenBid(s: Session | null): boolean {
  return !!s && (s.role === "manager" || s.role === "bidteam" || isStudioAdmin(s));
}

/** The Project Manager. Studio Admins and Site Admins get the whole list; a
    project-scoped member gets their own room only (checked per route). */
export function canOpenPm(s: Session | null): boolean {
  return !!s && (s.role === "manager" || s.role === "contractor" || s.role === "pclient" || isStudioAdmin(s));
}

/** Sees every project, not just one — the Projects list and cross-project work. */
export function pmFullAccess(s: Session | null): boolean {
  if (!s) return false;
  if (s.role === "manager" || isStudioAdmin(s) || isCommsManager(s)) return true;
  return s.role === "contractor" && fullAccess(s);
}
